import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Control, Label, Select } from "rbx";

import { useQuery } from "@apollo/client";
import {
  ALL_EMPLOYEE_RELATIONS_QUERY,
  ALL_EMPLOYEE_JOB_TITLES_QUERY,
} from "../../graphql";

// Filters out all titles in employee relations that already have a supervisor
const EmployeeTitleSelect = ({
  label,
  size,
  name,
  onChange,
  disabled,
  value,
  required,
  getValue,
  setValue,
  showBlank,
  showAll,
}) => {
  const { data: EmployeeTitleData, loading: EmployeeTitleLoading } =
    useQuery(ALL_EMPLOYEE_JOB_TITLES_QUERY, {
      variables: {
        where: {},
        distinct: "jobTitle",
        orderBy: [
          {
            jobTitle: "asc",
          },
        ],
      },
    });

  const { data: EmployeeRelationsData, loading: EmployeeRelationsLoading } =
    useQuery(ALL_EMPLOYEE_RELATIONS_QUERY, { fetchPolicy: "network-only" });

  const [options, setOptions] = useState([]);
  const [initialValue, setInitialValue] = useState(null);

  useEffect(() => {
    // set initial value
    if (value && !initialValue) {
      setInitialValue(value);
    }
  }, [initialValue, value]);

  useEffect(() => {
    if (
      EmployeeRelationsData?.findManyEmployeeRelations &&
      EmployeeTitleData?.users
    ) {
      // filter out all titles in employee relations that already have a supervisor
      const filterPrevSelected = EmployeeTitleData.users.filter(
        (employee) => {
          const previousSelect =
            !EmployeeRelationsData.findManyEmployeeRelations.find(
              (relation) => relation.title === employee.jobTitle
            );

          const isInput = employee.jobTitle === initialValue;

          return isInput || previousSelect;
        }
      );

      setOptions(filterPrevSelected);
    }
  }, [EmployeeRelationsData, EmployeeTitleData, initialValue]);

  const indicateRequired =
    required && (!value || value === "" || value === "0" || value === 0);

  const handleChange = (e) => {
    onChange(e.target.name, setValue(e.target.value));
  };

  return (
    <Control expanded size={size}>
      {label && <Label size={size}>{label}</Label>}
      <Select.Container
        fullwidth
        size={size}
        state={
          EmployeeTitleLoading ||
          EmployeeRelationsLoading ||
          Array.isArray(options)
            ? ""
            : "loading"
        }
      >
        <Select
          disabled={disabled}
          name={name}
          required={required}
          style={{
            border: indicateRequired ? "1px #e63362 solid" : "",
            borderRadius: "4px",
            boxShadow: indicateRequired
              ? "0 0 0 0.125em rgba(230, 51, 98, 0.25)"
              : "",
          }}
          value={getValue(value)}
          onChange={handleChange}
        >
          {showBlank && <Select.Option value="" />}
          {showAll && <Select.Option value="">All</Select.Option>}
          {Array.isArray(options) &&
            options.map(({ jobTitle }) => (
              <Select.Option key={jobTitle} value={jobTitle}>
                {jobTitle}
              </Select.Option>
            ))}
        </Select>
      </Select.Container>
    </Control>
  );
};

EmployeeTitleSelect.propTypes = {
  value: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.string,
  size: PropTypes.string,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  required: PropTypes.bool,
  getValue: PropTypes.func,
  setValue: PropTypes.func,
  showBlank: PropTypes.bool,
  showAll: PropTypes.bool,
};

EmployeeTitleSelect.defaultProps = {
  size: "normal",
  disabled: false,
  value: "",
  name: "",
  label: "",
  required: false,
  showBlank: true,
  showAll: false,
  getValue: (x) => x,
  setValue: (x) => x,
  onChange: () => {},
};

export default EmployeeTitleSelect;
